import api, { API } from "@/lib/api";

// Keep each chunk small enough to pass through the ingress/proxy body limit.
// (Large chunks were the reason big files, videos and some images failed.)
const CHUNK_SIZE = 512 * 1024; // 512KB
const MAX_RETRIES = 4;

async function postChunkWithRetry(uploadId, buffer) {
  let lastErr;
  for (let attempt = 0; attempt < MAX_RETRIES; attempt += 1) {
    try {
      await api.post(`/attachments/${uploadId}/chunk`, buffer, {
        headers: { "Content-Type": "application/octet-stream" },
        maxBodyLength: Infinity,
        maxContentLength: Infinity,
        timeout: 60000,
      });
      return;
    } catch (e) {
      lastErr = e;
      await new Promise((r) => setTimeout(r, 400 * (attempt + 1)));
    }
  }
  throw lastErr;
}

// Upload a File/Blob in chunks. Returns the stored attachment metadata.
export async function uploadFile(file, { name, onProgress } = {}) {
  const filename = name || file.name || "upload.bin";
  const contentType = file.type || "application/octet-stream";
  const size = file.size || 0;
  if (size === 0) throw new Error("File is empty");

  const init = await api.post("/attachments/init", {
    filename,
    content_type: contentType,
    size,
  });
  const uploadId = init.data.upload_id;

  let offset = 0;
  while (offset < size) {
    const slice = file.slice(offset, offset + CHUNK_SIZE);
    const buffer = await slice.arrayBuffer();
    await postChunkWithRetry(uploadId, buffer);
    offset += CHUNK_SIZE;
    onProgress?.(Math.min(1, offset / size));
  }

  const done = await api.post(`/attachments/${uploadId}/complete`, null, {
    timeout: 120000,
  });
  return done.data; // { id, filename, content_type, kind, size, url }
}

export function attachmentUrl(att, { download = false } = {}) {
  if (!att?.id) return "";
  return `${API}/attachments/${att.id}${download ? "?dl=1" : ""}`;
}

export function humanSize(bytes) {
  const b = Number(bytes) || 0;
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / (1024 * 1024)).toFixed(1)} MB`;
  return `${(b / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}
